import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import * as Icons from 'lucide-react';
import { Seo } from '@/components/site/Seo';
import { useAuth, formatApiError } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, login, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const isAdmin = user && user.role === 'admin';

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Signed in');
    } catch (err) {
      setLoginError(formatApiError(err, 'Sign in failed.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/estimates`, { withCredentials: true });
        setItems(res.data.items || []);
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          toast.error('Session expired — sign in again');
          await logout();
        } else toast.error('Failed to load submissions');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setSelected({ id });
    try {
      const res = await axios.get(`${API}/admin/estimates/${id}`, { withCredentials: true });
      setSelected(res.data);
    } catch {
      toast.error('Failed to load estimate');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Still checking session
  if (user === null) {
    return (
      <section className="min-h-[70vh] flex items-center bg-brand-bone" data-testid="admin-loading">
        <div className="container-x max-w-md font-sans text-sm text-brand-muted">Checking session…</div>
      </section>
    );
  }

  if (!user) {
    return (
      <section className="min-h-[70vh] flex items-center bg-brand-bone" data-testid="admin-login">
        <Seo title="Admin | HearthOak Construction" noindex path="/admin" />
        <div className="container-x max-w-md">
          <div className="eyebrow mb-3">Admin</div>
          <h1 className="h2">Sign in</h1>
          <p className="body-lg mt-3">Sign in with the owner account to view estimate submissions.</p>
          <form onSubmit={onLogin} className="mt-8 space-y-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="hoc-input"
              data-testid="admin-email-input"
              autoFocus
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="hoc-input"
              data-testid="admin-password-input"
            />
            {loginError && <p className="font-sans text-sm text-brand-maroon" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="admin-login-submit">
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="min-h-[70vh] flex items-center bg-brand-bone" data-testid="admin-forbidden">
        <Seo title="Admin | HearthOak Construction" noindex path="/admin" />
        <div className="container-x max-w-md">
          <div className="eyebrow mb-3">Admin</div>
          <h1 className="h2">No admin access</h1>
          <p className="body-lg mt-3">This account ({user.email}) does not have admin access.</p>
          <button onClick={logout} className="btn-secondary mt-6" data-testid="admin-forbidden-logout">Sign out</button>
        </div>
      </section>
    );
  }

  return (
    <div className="min-h-[80vh] bg-brand-bone" data-testid="page-admin">
      <Seo title="Admin | HearthOak Construction" noindex path="/admin" />
      <div className="container-x py-10 lg:py-16">
        <div className="flex items-center justify-between border-b border-brand-border pb-6">
          <div>
            <div className="eyebrow mb-2">Admin</div>
            <h1 className="h2">Estimate submissions</h1>
          </div>
          <button onClick={logout} className="btn-secondary" data-testid="admin-logout">Sign out</button>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
          <div className="border border-brand-border bg-brand-bone">
            <div className="p-4 border-b border-brand-border flex items-center justify-between">
              <span className="font-sans text-sm uppercase tracking-wider">All submissions</span>
              <span className="font-sans text-xs text-brand-muted">{items.length}</span>
            </div>
            <ul className="max-h-[70vh] overflow-y-auto" data-testid="admin-list">
              {loading && <li className="p-6 text-sm text-brand-muted">Loading…</li>}
              {!loading && items.length === 0 && (
                <li className="p-6 text-sm text-brand-muted">No submissions yet.</li>
              )}
              {items.map((it) => (
                <li key={it.id}>
                  <button
                    onClick={() => openDetail(it.id)}
                    className={`w-full text-left p-4 border-b border-brand-border hover:bg-brand-boneAlt transition-colors ${selected?.id === it.id ? 'bg-brand-boneAlt' : ''}`}
                    data-testid={`admin-item-${it.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-serif text-base text-brand-charcoal">{it.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-brand-muted">{formatDate(it.created_at)}</div>
                    </div>
                    <div className="mt-1 text-xs text-brand-muted font-sans">{it.service_needed} · {it.address_or_city}</div>
                    <div className="mt-1 text-xs text-brand-charcoal font-sans">{it.phone} · {it.email}</div>
                    {it.photo_count > 0 && (
                      <div className="mt-1 text-[10px] uppercase tracking-wider text-brand-maroon">{it.photo_count} photo{it.photo_count > 1 ? 's' : ''}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-brand-border bg-brand-bone p-6 lg:p-8 min-h-[300px]">
            {!selected && (
              <div className="text-brand-muted font-sans text-sm h-full flex items-center justify-center">
                Select a submission on the left to view details.
              </div>
            )}
            {selected && detailLoading && <div className="text-brand-muted">Loading…</div>}
            {selected && !detailLoading && selected.name && (
              <div data-testid="admin-detail">
                <div className="eyebrow">Submitted {formatDate(selected.created_at)}</div>
                <h2 className="h3 mt-2">{selected.name}</h2>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 font-sans text-sm">
                  <Info label="Phone" value={selected.phone} />
                  <Info label="Email" value={selected.email} />
                  <Info label="Location" value={selected.address_or_city} />
                  <Info label="Service" value={selected.service_needed} />
                  <Info label="Timeline" value={selected.preferred_timeline || '—'} />
                  <Info label="Best contact" value={selected.best_contact} />
                </div>
                <div className="mt-6">
                  <div className="eyebrow mb-2">Description</div>
                  <p className="whitespace-pre-wrap font-sans text-sm leading-relaxed border-l-2 border-brand-maroon bg-brand-boneAlt p-4">{selected.project_description}</p>
                </div>
                {selected.photos && selected.photos.length > 0 && (
                  <div className="mt-6">
                    <div className="eyebrow mb-3">Photos</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selected.photos.map((p, i) => (
                        <a
                          key={i}
                          href={`data:${p.content_type};base64,${p.data_base64}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="border border-brand-border block bg-white"
                        >
                          <img src={`data:${p.content_type};base64,${p.data_base64}`} alt={p.filename} className="w-full aspect-square object-cover" />
                          <div className="p-2 text-xs font-sans truncate">{p.filename}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-8 flex flex-wrap gap-3">
                  <a href={`tel:${(selected.phone || '').replace(/[^0-9+]/g, '')}`} className="btn-primary inline-flex items-center gap-2"><Icons.Phone size={14}/> Call</a>
                  <a href={`mailto:${selected.email}`} className="btn-secondary inline-flex items-center gap-2"><Icons.Mail size={14}/> Email</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">{label}</div>
      <div className="mt-1 font-serif text-base text-brand-charcoal break-words">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}
